import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "../lib/api";
import { ChairCard } from "../components/ChairCard";
import { AdSlot } from "../components/AdSlot";

const FacetGroup = ({ title, options, value, param, setParam, getLabel = (o) => o, getVal = (o) => o }) => (
  <div className="py-5 border-b border-border">
    <p className="overline mb-3">{title}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const v = getVal(o);
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => setParam(param, active ? "" : v)}
            className={`text-xs px-3 py-1.5 border transition-colors ${active ? "bg-accent text-accent-foreground border-accent" : "border-border bg-card hover:border-accent"}`}
            data-testid={`facet-${param}-${v}`}
          >
            {getLabel(o)}
          </button>
        );
      })}
    </div>
  </div>
);

export default function Catalog() {
  const [sp, setSp] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const params = Object.fromEntries(sp.entries());

  const setParam = useCallback((key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next);
  }, [sp, setSp]);

  useEffect(() => { api.get("/facets").then((r) => setFacets(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/chairs", { params: { ...params, limit: 60 } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [sp]); // eslint-disable-line

  const activeCount = ["era", "designer_slug", "movement_slug", "material", "style", "search"].filter((k) => params[k]).length;

  const Filters = () => facets && (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="serif text-2xl">Filters</p>
        {activeCount > 0 && (
          <button onClick={() => setSp(new URLSearchParams())} className="text-xs text-accent hover:underline" data-testid="clear-filters">Clear all</button>
        )}
      </div>
      <FacetGroup title="Era" options={facets.eras} value={params.era} param="era" setParam={setParam} />
      <FacetGroup title="Movement" options={facets.movements} value={params.movement_slug} param="movement_slug" setParam={setParam} getLabel={(o) => o.name} getVal={(o) => o.slug} />
      <FacetGroup title="Designer" options={facets.designers} value={params.designer_slug} param="designer_slug" setParam={setParam} getLabel={(o) => o.name} getVal={(o) => o.slug} />
      <FacetGroup title="Material" options={facets.materials} value={params.material} param="material" setParam={setParam} />
      <FacetGroup title="Style" options={facets.styles} value={params.style} param="style" setParam={setParam} />
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline">The Collection</p>
          <h1 className="serif text-4xl sm:text-5xl tracking-tight mt-1">Catalog</h1>
          {params.search && <p className="text-sm text-muted-foreground mt-2">Results for “{params.search}”</p>}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={params.sort || "title"}
            onChange={(e) => setParam("sort", e.target.value === "title" ? "" : e.target.value)}
            className="border border-border bg-card text-sm h-10 px-3"
            data-testid="sort-select"
          >
            <option value="title">A–Z</option>
            <option value="year_asc">Oldest first</option>
            <option value="year_desc">Newest first</option>
          </select>
          <button onClick={() => setShowFilters(true)} className="lg:hidden inline-flex items-center gap-2 border border-border bg-card h-10 px-4 text-sm" data-testid="open-filters">
            <SlidersHorizontal size={15} /> Filters{activeCount ? ` (${activeCount})` : ""}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block sticky top-20 self-start"><Filters /></aside>

        <div>
          <p className="text-sm text-muted-foreground mb-6" data-testid="results-count">{data.total} chairs</p>
          {loading ? (
            <p className="text-muted-foreground py-20 text-center">Loading…</p>
          ) : data.items.length === 0 ? (
            <p className="text-muted-foreground py-20 text-center" data-testid="no-results">No chairs match these filters.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-10">
              {data.items.map((c, i) => <ChairCard key={c.slug} chair={c} index={i} />)}
            </div>
          )}
          <div className="mt-12"><AdSlot height="h-28" /></div>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-background p-6 overflow-y-auto">
            <button onClick={() => setShowFilters(false)} className="absolute top-4 right-4 p-2" data-testid="close-filters"><X size={20} /></button>
            <Filters />
            <button onClick={() => setShowFilters(false)} className="w-full bg-primary text-primary-foreground h-12 mt-6 text-sm">Show {data.total} chairs</button>
          </div>
        </div>
      )}
    </div>
  );
}
